/**
 * Validated navigation into a student's record, which lives on the Enrollments
 * roster, and back out to the workspaces that link to it.
 */
import { type RoleId } from '@shared/roles';
import {
  INBOX_ORIGIN_PARAM,
  inboxReturnPath,
} from '@/components/admin/attendance/attendanceVocabulary';
import {
  USER_RECORD_TABS,
  validatedUserReturnTo,
} from '@/components/admin/userManagement/userNavigation';
import { hasRouteAccess } from '@/utils/accessControl';

import {
  CLASS_CODES_PATH,
  classCodesReturnTo,
  parseClassCodesListParams,
} from '../classCodes/classCodesListParams';
import {
  readRosterUrlState,
  rosterUrlQuery,
} from '../enrollments/rosterUrlState';

/** Legacy class Billing links name the student with `?participant=`. */
export const PARTICIPANT_PARAM = 'participant';
/** `?returnTo=` — where the class page's back button lands. */
export const RETURN_TO_PARAM = 'returnTo';

export const LIVE_CLASSES_PATH = '/admin/live';
export const ENROLLMENTS_PATH = '/admin/enrollments';
const ATTENDANCE_INBOX_PATH = '/admin/attendance';
/** One user record: `/admin/users/:userId`, with the id URL-encoded. */
const USER_RECORD_PATH = /^\/admin\/users\/[^/]+$/;
const OBJECT_ID = /^[a-f\d]{24}$/i;

export interface ClassDetailOrigin {
  href: string;
  label: string;
}

/**
 * The lists allowed to be a class page's origin. Each rebuilds its own href
 * from the untrusted query, echoing only the params that page honours, and
 * names the route row that decides whether the role can open it.
 */
interface OriginKind {
  matches: (pathname: string) => boolean;
  /** The route key `hasRouteAccess` checks — literal, as in `ROUTE_ACCESS`. */
  routeKey: string;
  label: string;
  normalize: (
    pathname: string,
    query: URLSearchParams,
    depth: number
  ) => string;
}

const ORIGIN_KINDS: readonly OriginKind[] = [
  {
    matches: pathname => /^\/admin\/classes\/[a-f\d]{24}$/i.test(pathname),
    routeKey: '/admin/classes/:classCodeId',
    label: 'Class',
    normalize: (pathname, query, depth) => {
      const params = new URLSearchParams({
        tab: query.get('tab') === 'overview' ? 'overview' : 'attendance',
      });
      const session = query.get('session');
      if (session && OBJECT_ID.test(session)) params.set('session', session);
      const origin = validatedBillingReturnTo(query.get('returnTo'), depth + 1);
      if (origin) params.set('returnTo', origin);
      const from = query.get(INBOX_ORIGIN_PARAM);
      if (from === 'completed' || /^\d{4}-\d{2}-\d{2}$/.test(from ?? ''))
        params.set(INBOX_ORIGIN_PARAM, from!);
      return `${pathname}?${params}`;
    },
  },
  {
    matches: pathname => pathname === LIVE_CLASSES_PATH,
    routeKey: LIVE_CLASSES_PATH,
    label: 'Live classes',
    normalize: () => LIVE_CLASSES_PATH,
  },
  {
    matches: pathname => pathname === CLASS_CODES_PATH,
    routeKey: CLASS_CODES_PATH,
    label: 'Class Codes',
    normalize: (_pathname, query) => {
      const filters = parseClassCodesListParams(query);
      return classCodesReturnTo({ ...filters, q: filters.q.slice(0, 64) });
    },
  },
  {
    matches: pathname => pathname === ENROLLMENTS_PATH,
    routeKey: ENROLLMENTS_PATH,
    label: 'Enrollments',
    normalize: (_pathname, query, depth) => enrollmentsHref(query, depth),
  },
  {
    matches: pathname => pathname === ATTENDANCE_INBOX_PATH,
    routeKey: ATTENDANCE_INBOX_PATH,
    label: 'Attendance',
    // Rebuilt through the inbox's own token → path mapping, so only its three
    // views can be named, exactly as `?from=` is.
    normalize: (_pathname, query) =>
      inboxReturnPath(
        query.get('view') === 'completed' ? 'completed' : query.get('date')
      ),
  },
  {
    matches: pathname => USER_RECORD_PATH.test(pathname),
    routeKey: '/admin/users/:userId',
    label: 'User record',
    // A user record carries its own origin; that contract's validator owns it.
    normalize: (pathname, query) => {
      const next = new URLSearchParams();
      const tab = query.get('tab');
      if (tab && (USER_RECORD_TABS as readonly string[]).includes(tab))
        next.set('tab', tab);
      if (query.has('returnTo'))
        next.set('returnTo', validatedUserReturnTo(query.get('returnTo')));
      const search = next.toString();
      return search ? `${pathname}?${search}` : pathname;
    },
  },
];

/**
 * The class page's back-button destination, rebuilt from an untrusted value.
 * Anything absolute, protocol-relative, backslashed, or off the allowlist is
 * `null`, and the caller falls back to the role's default list. Mirrors
 * `validatedUserReturnTo`, minus the fallback: which list is "home" depends on
 * the role, and `classDetailOrigin` is where that is decided.
 */
export function validatedBillingReturnTo(
  value: string | null | undefined,
  depth = 0
): string | null {
  if (
    !value ||
    depth > 3 ||
    value.length > 12000 ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  )
    return null;
  const [pathname, query = ''] = value.split('?');
  const kind = ORIGIN_KINDS.find(candidate => candidate.matches(pathname));
  return kind
    ? kind.normalize(pathname, new URLSearchParams(query), depth)
    : null;
}

/**
 * Where the class page's back button goes and what it says, from the one
 * validated origin (see the diagram in the header). A valid `returnTo` the role
 * can open wins; then the inbox's `from` token, for a role that can open the
 * inbox; then the default list for the role.
 */
export function classDetailOrigin(
  searchParams: URLSearchParams,
  userRole: RoleId | string | null | undefined
): ClassDetailOrigin {
  const returnTo = validatedBillingReturnTo(searchParams.get(RETURN_TO_PARAM));
  if (returnTo) {
    const [pathname] = returnTo.split('?');
    const kind = ORIGIN_KINDS.find(candidate => candidate.matches(pathname));
    if (kind && hasRouteAccess(userRole, kind.routeKey))
      return { href: returnTo, label: kind.label };
  }
  if (hasRouteAccess(userRole, ATTENDANCE_INBOX_PATH)) {
    return {
      href: inboxReturnPath(searchParams.get(INBOX_ORIGIN_PARAM)),
      label: 'Attendance',
    };
  }
  return { href: ENROLLMENTS_PATH, label: 'Enrollments' };
}

/**
 * A class's billing: the roster filtered to the class, or, given a student,
 * that student's record focused on the class.
 */
export function classBillingHref(
  classCodeId: string,
  {
    participantId,
    returnTo,
  }: { participantId?: string | null; returnTo?: string | null } = {}
): string {
  if (participantId)
    return studentBillingRecordHref(participantId, { returnTo, classCodeId });
  const params = new URLSearchParams({ class: classCodeId });
  const origin = validatedBillingReturnTo(returnTo);
  if (origin) params.set(RETURN_TO_PARAM, origin);
  return `${ENROLLMENTS_PATH}?${params}`;
}

/**
 * The Enrollments page rebuilt from an untrusted query, echoing only what it
 * honours: the view, the roster's filters, a pinned student and the class
 * their record focuses, and a validated way back.
 */
export function enrollmentsHref(query: URLSearchParams, depth = 0): string {
  const state = readRosterUrlState(query);
  const next = rosterUrlQuery(state);
  const origin = validatedBillingReturnTo(state.returnTo, depth + 1);
  if (origin) next.set(RETURN_TO_PARAM, origin);
  return `${ENROLLMENTS_PATH}${next.size ? `?${next}` : ''}`;
}

/**
 * Where an old Student Billing URL now lives (plan, "Old URLs"): the list's
 * Payments due view is that tab and its class the roster's Class filter; a
 * bare list, which was the Payments Due page, is that tab; a record becomes the
 * roster pinned to that student, focused on the class it named. The search and
 * the way back carry over.
 */
export function legacyBillingHref(
  query: URLSearchParams,
  participantId?: string
): string {
  const next = new URLSearchParams({
    returnTo: query.get(RETURN_TO_PARAM) ?? '',
  });
  const classCodeId = query.get('classCodeId') ?? '';
  if (participantId) {
    next.set('student', participantId);
    next.set('classCodeId', classCodeId);
  } else {
    next.set('class', classCodeId);
    for (const key of ['view', 'search']) next.set(key, query.get(key) ?? '');
    // Neither a view nor a class named: the bare list, which was Payments Due.
    // A view the page no longer has (`balances`; `payments` since the Payments
    // to check tab went) falls to the roster.
    if (!query.get('view') && !classCodeId) next.set('view', 'due');
  }
  return enrollmentsHref(next);
}

/** Open a class with an explicit, validated way back to its source list. */
export function classPageHref(
  classCodeId: string,
  {
    tab,
    returnTo,
  }: { tab?: 'overview' | 'billing'; returnTo?: string | null } = {}
): string {
  const params = new URLSearchParams();
  if (tab) params.set('tab', tab);
  const origin = validatedBillingReturnTo(returnTo);
  if (origin) params.set(RETURN_TO_PARAM, origin);
  const search = params.toString();
  return `/admin/classes/${encodeURIComponent(classCodeId)}${search ? `?${search}` : ''}`;
}

/**
 * A student's record: the roster pinned to them with the record open,
 * focused on one class when given. `participantId` is the billing participant
 * (`participantIdFor` in `studentBillingApi`), the user id or
 * `enrollment:<id>` before activation. `returnTo` is validated here as well as
 * where the page reads it, so a link never carries an origin the page would
 * only throw away.
 */
export function studentBillingRecordHref(
  participantId: string,
  {
    returnTo,
    classCodeId,
  }: { returnTo?: string | null; classCodeId?: string | null } = {}
): string {
  const params = new URLSearchParams({ student: participantId });
  if (classCodeId && OBJECT_ID.test(classCodeId))
    params.set('classCodeId', classCodeId);
  const origin = validatedBillingReturnTo(returnTo);
  if (origin) params.set(RETURN_TO_PARAM, origin);
  return `${ENROLLMENTS_PATH}?${params}`;
}
